import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Custom hook for subscription management
 *
 * Provides subscription status, trial information, and checkout functions
 * across the entire application.
 *
 * Usage:
 *   const { subscription, isTrialing, daysRemaining, loading, refreshSubscription } = useSubscription();
 */
export function useSubscription() {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  /**
   * Fetch subscription status from backend
   */
  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_URL}/api/user/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        setError(null);
      } else if (response.status === 404) {
        // No subscription yet - this is OK
        setSubscription(null);
        setError(null);
      } else {
        throw new Error('Failed to fetch subscription');
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, getAccessTokenSilently, API_URL]);

  // Fetch subscription on mount and when auth changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  /**
   * Start checkout session
   */
  const startCheckout = useCallback(async (plan = 'personal') => {
    try {
      setCheckoutLoading(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth0_sub: user.sub,
          email: user.email,
          plan,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to checkout URL (loading state will persist until redirect completes)
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Error starting checkout:', err);
      setCheckoutLoading(false); // Only clear on error; on success we're redirecting anyway
      throw err;
    }
  }, [getAccessTokenSilently, user, API_URL]);

  /**
   * Open customer portal
   */
  const openCustomerPortal = useCallback(async () => {
    try {
      setPortalLoading(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_URL}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth0_sub: user.sub,
          returnUrl: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json();

      // Redirect to customer portal (loading state will persist until redirect completes)
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Error opening customer portal:', err);
      setPortalLoading(false); // Only clear on error; on success we're redirecting anyway
      throw err;
    }
  }, [getAccessTokenSilently, user, API_URL]);

  /**
   * Sync subscription with Stripe
   * Fetches fresh data from Stripe and updates local state
   */
  const syncSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) {
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_URL}/api/user/subscription/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        setError(null);
        return data;
      } else if (response.status === 404) {
        // No subscription to sync
        setSubscription(null);
        setError(null);
        return null;
      } else {
        throw new Error('Failed to sync subscription');
      }
    } catch (err) {
      console.error('Error syncing subscription:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, getAccessTokenSilently, API_URL]);

  // Computed properties
  const hasSubscription = subscription?.hasSubscription || false;
  const isTrialing = subscription?.subscription?.is_trialing || false;
  const daysRemaining = subscription?.subscription?.days_remaining_in_trial || 0;
  const status = subscription?.subscription?.status || null;
  const isActive = status === 'active' || status === 'trialing';
  const trialEndingSoon = isTrialing && daysRemaining <= 3;

  return {
    subscription: subscription?.subscription || null,
    hasSubscription,
    isTrialing,
    daysRemaining,
    status,
    isActive,
    trialEndingSoon,
    loading,
    checkoutLoading,
    portalLoading,
    error,
    refreshSubscription: fetchSubscription,
    syncSubscription,
    startCheckout,
    openCustomerPortal,
  };
}

export default useSubscription;
